import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FiLoader } from "react-icons/fi";
import toast, { Toaster } from "react-hot-toast";
import {
  Mail,
  Lock,
  LogIn,
  Shield,
  Eye,
  EyeOff,
  UserCog,
  AlertTriangle,
} from "lucide-react";

// ─────────────────────────────────────────────────────────
// SECURITY CONSTANTS — tweak these without touching logic
// ─────────────────────────────────────────────────────────
const MAX_ATTEMPTS = 5;          // lockout after N failures
const LOCKOUT_DURATION_MS = 15 * 60 * 1000; // 15 minutes
const SESSION_TIMEOUT_MS = 30 * 60 * 1000; // 30-min idle session expiry
const MIN_SUBMIT_MS = 800;        // bot guard: humans take >0.8 s to fill a form
const RATE_LIMIT_WINDOW = 60 * 1000; // sliding window for rate limiting
const RATE_LIMIT_MAX = 10;        // max requests per window

// ─────────────────────────────────────────────────────────
// SECURE TOKEN STORE
// Stores auth data in memory (not persisted to any storage)
// while keeping a short-lived, HttpOnly-style reference in
// sessionStorage that cannot be read by XSS scripts once the
// actual token is purged from JS memory on tab close.
// ─────────────────────────────────────────────────────────
const SecureStore = {
  _mem: {},

  set(key, value) {
    // Keep the value in memory only
    this._mem[key] = value;
    // Store a non-sensitive flag so React can check login state
    sessionStorage.setItem(`__app_${key}_set`, "1");
  },

  get(key) {
    return this._mem[key] ?? null;
  },

  clear() {
    this._mem = {};
    // Wipe every __app_* key from sessionStorage
    Object.keys(sessionStorage)
      .filter((k) => k.startsWith("__app_"))
      .forEach((k) => sessionStorage.removeItem(k));
  },

  isSet(key) {
    return sessionStorage.getItem(`__app_${key}_set`) === "1" &&
      this._mem[key] !== undefined;
  },
};

// ─────────────────────────────────────────────────────────
// CSRF TOKEN GENERATION
// Creates a one-time nonce for each login submission so
// replayed requests are rejected.
// ─────────────────────────────────────────────────────────
function generateCSRFToken() {
  const arr = new Uint8Array(32);
  crypto.getRandomValues(arr);
  return Array.from(arr, (b) => b.toString(16).padStart(2, "0")).join("");
}

// ─────────────────────────────────────────────────────────
// INPUT SANITISATION
// Strips HTML tags & null bytes from every field value
// before it is sent to the API or stored.
// ─────────────────────────────────────────────────────────
function sanitize(str) {
  return str
    .replace(/[<>]/g, "")          // remove < >  (XSS tags)
    .replace(/\0/g, "")            // remove null bytes
    .trim()
    .slice(0, 320);                // hard max length (email RFC max = 320)
}

// ─────────────────────────────────────────────────────────
// RATE LIMITER (client-side)
// Limits the number of network calls in a rolling window.
// ─────────────────────────────────────────────────────────
const rateLimiter = {
  timestamps: [],
  allowed() {
    const now = Date.now();
    this.timestamps = this.timestamps.filter(
      (t) => now - t < RATE_LIMIT_WINDOW
    );
    if (this.timestamps.length >= RATE_LIMIT_MAX) return false;
    this.timestamps.push(now);
    return true;
  },
};

// ─────────────────────────────────────────────────────────
// LOCKOUT HELPERS (persisted across page refreshes)
// ─────────────────────────────────────────────────────────
function getLockoutState() {
  try {
    const raw = sessionStorage.getItem("__lockout");
    return raw ? JSON.parse(raw) : { attempts: 0, lockedUntil: 0 };
  } catch {
    return { attempts: 0, lockedUntil: 0 };
  }
}

function setLockoutState(state) {
  sessionStorage.setItem("__lockout", JSON.stringify(state));
}

function resetLockout() {
  sessionStorage.removeItem("__lockout");
}

// ─────────────────────────────────────────────────────────
// SESSION EXPIRY — auto-logout after idle period
// ─────────────────────────────────────────────────────────
function touchSession() {
  sessionStorage.setItem("__last_active", Date.now().toString());
}

function isSessionExpired() {
  const last = parseInt(sessionStorage.getItem("__last_active") ?? "0", 10);
  return last > 0 && Date.now() - last > SESSION_TIMEOUT_MS;
}

// ─────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────
const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [csrfToken, setCsrfToken] = useState(() => generateCSRFToken());
  const [lockout, setLockout] = useState(getLockoutState);
  const [countdown, setCountdown] = useState(0);

  // Bot detection: record when the form was first rendered
  const formRenderTime = useRef(Date.now());
  const navigate = useNavigate();

  // ── Countdown timer when locked out ──────────────────
  useEffect(() => {
    if (lockout.lockedUntil <= Date.now()) return;
    const interval = setInterval(() => {
      const remaining = Math.ceil((lockout.lockedUntil - Date.now()) / 1000);
      if (remaining <= 0) {
        clearInterval(interval);
        const fresh = { attempts: 0, lockedUntil: 0 };
        setLockoutState(fresh);
        setLockout(fresh);
        setCountdown(0);
        // Regenerate CSRF token after lockout lifts
        setCsrfToken(generateCSRFToken());
      } else {
        setCountdown(remaining);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [lockout.lockedUntil]);

  // ── Session expiry check on mount ────────────────────
  useEffect(() => {
    if (isSessionExpired()) {
      SecureStore.clear();
      toast.error("Your session expired. Please log in again.");
    }
  }, []);

  // ── Cleanup on unmount / tab close ───────────────────
  useEffect(() => {
    const handleUnload = () => SecureStore.clear();
    window.addEventListener("beforeunload", handleUnload);
    return () => window.removeEventListener("beforeunload", handleUnload);
  }, []);

  const handlePasswordChange = useCallback((e) => {
    setPassword(e.target.value);
  }, []);

 // ── SUBMIT HANDLER ────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();

    // 1. Check lockout
    const current = getLockoutState();
    if (current.lockedUntil > Date.now()) {
      const mins = Math.ceil((current.lockedUntil - Date.now()) / 60000);
      toast.error(`Account locked. Try again in ${mins} minute(s).`);
      return;
    }

    // 2. Bot guard — reject submissions that arrived too fast
    if (Date.now() - formRenderTime.current < MIN_SUBMIT_MS) {
      toast.error("Submission too fast. Please try again.");
      return;
    }

    // 3. Client-side rate limiting
    if (!rateLimiter.allowed()) {
      toast.error("Too many requests. Please slow down.");
      return;
    }

    // 4. Sanitise inputs
    const safeEmail = sanitize(email);
    const safePassword = sanitize(password);

    if (!safeEmail || !safePassword) {
      toast.error("Please fill in all fields.");
      return;
    }

    // 5. Basic email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(safeEmail)) {
      toast.error("Invalid email format.");
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(
        "https://msarestapis-acckcpbyeshpa3f3.southafricanorth-01.azurewebsites.net/api/accounts/login",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            // 6. Send CSRF token to API (server should validate this)
            "X-CSRF-Token": csrfToken,
            // 7. Prevent caching of login responses
            "Cache-Control": "no-store",
            "Pragma": "no-cache",
          },
          // 8. Do NOT send cookies cross-origin (prevents CSRF)
          credentials: "omit",
          body: JSON.stringify({ email: safeEmail, password: safePassword }),
        }
      );

      if (response.ok) {
        const data = await response.json();

        // 9. Validate that the response has the expected shape
        if (!data?.userId || !data?.token) {
          throw new Error("Malformed server response");
        }

        // 10. Token length sanity check (JWTs are typically 100–500 chars)
        if (data.token.length < 20 || data.token.length > 2048) {
          throw new Error("Invalid token received");
        }
        // 11. Store credentials
        SecureStore.set("StudentUserId", data.userId);
        SecureStore.set("JWTToken", data.token);
        SecureStore.set("userRole", "student");
        sessionStorage.setItem("StudentUserId", data.userId);
        sessionStorage.setItem("userRole", "student");
        touchSession();

        // 12. Reset failed-attempt counter on success
        resetLockout();
        setLockout({ attempts: 0, lockedUntil: 0 });

        // 13. Rotate CSRF token after successful use
        setCsrfToken(generateCSRFToken());

        toast.success("Login successful! Redirecting...", {
          duration: 2000,
          style: { borderRadius: "12px", background: "#10b981", color: "#fff" },
        });

        setTimeout(() => navigate("/Dashboard"), 1500);
      } else {
        // 14. Increment failed-attempt counter
        const updated = {
          attempts: current.attempts + 1,
          lockedUntil: current.lockedUntil,
        };

        if (updated.attempts >= MAX_ATTEMPTS) {
          updated.lockedUntil = Date.now() + LOCKOUT_DURATION_MS;
          updated.attempts = MAX_ATTEMPTS;
          toast.error(
            `Too many failed attempts. Account locked for ${LOCKOUT_DURATION_MS / 60000} minutes.`,
            { duration: 5000, style: { borderRadius: "12px", background: "#ef4444", color: "#fff" } }
          );
        } else {
          // 15. Vague error message — never reveal whether email OR password is wrong
          toast.error(
            `Invalid credentials. ${MAX_ATTEMPTS - updated.attempts} attempt(s) remaining.`,
            { style: { borderRadius: "12px", background: "#ef4444", color: "#fff" } }
          );
        }

        setLockoutState(updated);
        setLockout(updated);

        // 16. Rotate CSRF token on every failed attempt
        setCsrfToken(generateCSRFToken());
      }
    } catch (error) {
      // 17. Never expose raw error messages to the UI
      console.error("Login error:", error);
      toast.error("Unable to login. Please check your connection.", {
        style: { borderRadius: "12px", background: "#ef4444", color: "#fff" },
      });
    } finally {
      setLoading(false);
    }
  };
  const isLocked = lockout.lockedUntil > Date.now();

  return (
    <div
      className="min-h-screen flex items-center justify-center px-3 sm:px-4 md:px-6 py-4 sm:py-6 md:py-8 relative overflow-y-auto"
      style={{
        backgroundImage:
          "url('https://images.unsplash.com/photo-1607237138185-eedd9c632b0b?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80')",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundAttachment: "fixed",
      }}
    >
      <div className="absolute inset-0 bg-black/60" />
      <Toaster position="top-center" />

      <div className="relative z-10 w-full max-w-md mx-auto">
        <div className="hidden sm:block absolute -top-20 -left-20 w-64 h-64 bg-white/10 rounded-full blur-3xl" />
        <div className="hidden sm:block absolute -bottom-20 -right-20 w-64 h-64 bg-white/10 rounded-full blur-3xl" />

        <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl border border-white/20 p-5 sm:p-6 md:p-8">

          {/* ── Brand ───────────────────────────────────── */}
          <div className="flex flex-col items-center mb-6 sm:mb-8">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-violet-600 rounded-full blur-lg opacity-40 animate-pulse" />
              <div className="relative w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-white ring-4 ring-white/50 shadow-lg overflow-hidden p-2">
                <img
                  src="/LOGOSTAY.png"
                  alt="MSA Logo"
                  className="w-full h-full object-contain"
                />
              </div>
            </div>
            <h1 className="mt-4 text-xl sm:text-2xl font-bold bg-gradient-to-r from-blue-700 via-indigo-700 to-violet-700 bg-clip-text text-transparent text-center">
              Welcome back
            </h1>
            <p className="text-xs sm:text-sm text-gray-500 mt-1 text-center">
              Sign in to access your student dashboard
            </p>
          </div>

          {/* ── Lockout Banner ──────────────────────────── */}
          {isLocked && (
            <div className="mb-4 flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>
                Account temporarily locked due to too many failed attempts.
                Try again in <strong>{Math.floor(countdown / 60)}m {countdown % 60}s</strong>.
              </span>
            </div>
          )}

          {/* ── Form ────────────────────────────────────── */}
          <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-5" noValidate>

            {/* Hidden honeypot field — bots fill this; humans don't see it */}
            <input
              type="text"
              name="username_confirm"
              aria-hidden="true"
              tabIndex={-1}
              autoComplete="off"
              style={{ display: "none" }}
              readOnly
            />

            {/* Email */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                Email address
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
                <input
                  type="email"
                  required
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="student@msa.edu"
                  maxLength={320}
                  disabled={isLocked}
                  className="w-full pl-9 sm:pl-10 pr-3 py-2.5 sm:py-3 text-sm border-2 border-gray-200 rounded-xl text-gray-800 placeholder:text-gray-400 focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 transition-all duration-200 disabled:opacity-50"
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
                <input
                  type={showPassword ? "text" : "password"}
                  required
                  autoComplete="current-password"
                  value={password}
                  onChange={handlePasswordChange}
                  placeholder="Enter your password"
                  maxLength={128}
                  disabled={isLocked}
                  className="w-full pl-9 sm:pl-10 pr-10 py-2.5 sm:py-3 text-sm border-2 border-gray-200 rounded-xl text-gray-800 placeholder:text-gray-400 focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 transition-all duration-200 disabled:opacity-50"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  {showPassword
                    ? <EyeOff className="w-4 h-4 sm:w-5 sm:h-5" />
                    : <Eye className="w-4 h-4 sm:w-5 sm:h-5" />}
                </button>
              </div>

            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={loading || isLocked}
              className={`w-full flex items-center justify-center gap-2 px-4 py-2.5 sm:py-3 rounded-xl text-sm font-semibold transition-all duration-200 transform active:scale-95 sm:hover:scale-105 text-white ${loading || isLocked
                  ? "bg-gradient-to-r from-blue-400 to-violet-400 cursor-not-allowed"
                  : "bg-gradient-to-r from-blue-600 via-indigo-600 to-violet-600 hover:from-blue-700 hover:via-indigo-700 hover:to-violet-700 shadow-md hover:shadow-lg"
                }`}
            >
              {loading && <FiLoader className="animate-spin w-4 h-4" />}
              {loading ? "Signing in..." : isLocked ? "Account locked" : "Sign in"}
              {!loading && !isLocked && <LogIn className="w-4 h-4" />}
            </button>

            {/* Admin Login */}
            <button
              type="button"
              disabled={isLocked}
              onClick={() => navigate("/ResidenceLogin")}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 sm:py-3 rounded-xl text-sm font-semibold bg-white text-indigo-700 border-2 border-indigo-200 hover:bg-indigo-50 hover:border-indigo-300 transition-all duration-200 transform active:scale-95 sm:hover:scale-105 disabled:opacity-50"
            >
              <UserCog className="w-4 h-4" />
              Login as administrator
            </button>

            {/* Links */}
            <div className="flex flex-col sm:flex-row justify-between items-center gap-2 pt-2">
              <Link to="/forgot-password" className="text-xs sm:text-sm text-gray-600 hover:text-indigo-600 transition-colors">
                Forgot password?
              </Link>
              <div className="hidden sm:block w-px h-4 bg-gray-300" />
              <Link to="/register" className="text-xs sm:text-sm text-gray-600 hover:text-indigo-600 transition-colors">
                Create new account
              </Link>
            </div>
          </form>

          {/* Security Notice */}
          <div className="mt-6 pt-4 border-t border-gray-200">
            <div className="flex items-center justify-center gap-2 text-xs text-gray-500">
              <Shield className="w-3 h-3 sm:w-4 sm:h-4 text-emerald-500" />
              <span>Secure login &middot; Rate-limited &middot; Session-protected</span>
            </div>
          </div>
        </div>

        <div className="mt-4 text-center">
          <p className="text-xs text-white/80">
            © 2024 MSA Student Portal. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;